<script setup lang="ts">
  import { ExclamationCircleIcon } from '@heroicons/vue/solid';
  import TopToRightAngleIcon from '~/assets/Icons/TopToRightAngleIcon.vue';
  import { formatToIndianCurrency, formatToIndianCurrencyBadge } from '~/functions/currencyUtilities';
  import type { ExpensesStatus } from '~/types';

  const props = defineProps<{
    stats: ExpensesStatus;
    enterpriseVersion?: boolean;
  }>();

  const totalGrantAmount = computed(() => {
    const value = props.stats.total_grant_amount ? +props.stats.total_grant_amount : 0;
    return isNaN(value) ? 0 : value;
  });

  // Approved (B)
  const approvedExpenses = computed(() => {
    const value = +props.stats.approved_expenses.within_grant + +props.stats.approved_expenses.exceeding_grant;
    return isNaN(value) ? 0 : value;
  });
  const approvedWithinGrantsPercentage = computed(() => {
    const percentage = (+props.stats.approved_expenses.within_grant / totalGrantAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const approvedExceedingGrantPercentage = computed(() => {
    const percentage = (+props.stats.approved_expenses.exceeding_grant / totalGrantAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });

  // Pending / On Approval (C)
  const pendingExpenses = computed(() => {
    const within = +(props.stats.pending_expenses?.pending_within_grant ?? 0);
    const exceeding = +(props.stats.pending_expenses?.pending_exceeding_grant ?? 0);
    const value = within + exceeding;
    return isNaN(value) ? 0 : value;
  });
  const pendingWithinGrantsPercentage = computed(() => {
    const percentage = (+(props.stats.pending_expenses?.pending_within_grant ?? 0) / totalGrantAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const pendingExceedingGrantPercentage = computed(() => {
    const percentage = (+(props.stats.pending_expenses?.pending_exceeding_grant ?? 0) / totalGrantAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });

  // Draft (D)
  const draftExpenses = computed(() => {
    const within = +(props.stats.draft_expenses?.draft_within_grant ?? 0);
    const exceeding = +(props.stats.draft_expenses?.draft_exceeding_grant ?? 0);
    const value = within + exceeding;
    return isNaN(value) ? 0 : value;
  });
  const draftWithinGrantsPercentage = computed(() => {
    const percentage = (+(props.stats.draft_expenses?.draft_within_grant ?? 0) / totalGrantAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const draftExceedingGrantPercentage = computed(() => {
    const percentage = (+(props.stats.draft_expenses?.draft_exceeding_grant ?? 0) / totalGrantAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });

  const currentBalance = computed(() => totalGrantAmount.value - approvedExpenses.value);

  const potentialBalance = computed(
    () => totalGrantAmount.value - approvedExpenses.value - pendingExpenses.value - draftExpenses.value
  );

  const percentageUtilized = computed(() => {
    const value = approvedWithinGrantsPercentage.value + approvedExceedingGrantPercentage.value;
    if (isNaN(value)) return 0;
    if (!isFinite(value)) return 0;
    return value;
  });

  const calculateScaledPercentage = (percentage: number, total: number) => {
    if (total <= 100) return percentage;
    return (percentage / total) * 100;
  };

  const totalPercentage = computed(() => {
    return (
      approvedWithinGrantsPercentage.value +
      approvedExceedingGrantPercentage.value +
      pendingWithinGrantsPercentage.value +
      pendingExceedingGrantPercentage.value +
      draftWithinGrantsPercentage.value +
      draftExceedingGrantPercentage.value
    );
  });

  const scaledApprovedWithinGrants = computed(() =>
    calculateScaledPercentage(approvedWithinGrantsPercentage.value, totalPercentage.value)
  );
  const scaledApprovedExceedingGrants = computed(() =>
    calculateScaledPercentage(approvedExceedingGrantPercentage.value, totalPercentage.value)
  );
  const scaledPendingWithinGrants = computed(() =>
    calculateScaledPercentage(pendingWithinGrantsPercentage.value, totalPercentage.value)
  );
  const scaledPendingExceedingGrants = computed(() =>
    calculateScaledPercentage(pendingExceedingGrantPercentage.value, totalPercentage.value)
  );
  const scaledDraftWithinGrants = computed(() =>
    calculateScaledPercentage(draftWithinGrantsPercentage.value, totalPercentage.value)
  );
  const scaledDraftExceedingGrants = computed(() =>
    calculateScaledPercentage(draftExceedingGrantPercentage.value, totalPercentage.value)
  );

  const isZeroGrant = computed(() => totalGrantAmount.value === 0);

  const hasExceedingExpenses = computed(
    () =>
      +props.stats.approved_expenses.exceeding_grant > 0 ||
      +(props.stats.pending_expenses?.pending_exceeding_grant ?? 0) > 0 ||
      +(props.stats.draft_expenses?.draft_exceeding_grant ?? 0) > 0
  );

  const pendingExceedingExists = computed(() => +(props.stats.pending_expenses?.pending_exceeding_grant ?? 0) > 0);
  const draftExceedingExists = computed(() => +(props.stats.draft_expenses?.draft_exceeding_grant ?? 0) > 0);
</script>

<template>
  <a-popover placement="bottom" :arrow="false">
    <template #content>
      <div class="w-[425px] flex flex-col py-2 px-3 gap-2 text-zinc-800">
        <!-- (A) Total Grant Amount -->
        <div class="flex justify-between items-center bg-[#F4F4F5] border border-[#E4E4E7] rounded px-2 py-1.5">
          <p class="text-sm font-bold">Total Grant Amount <span class="font-normal text-zinc-500">(A)</span></p>
          <p class="text-sm font-bold text-[#15803D]">{{ formatToIndianCurrency(totalGrantAmount) }}</p>
        </div>

        <!-- (B) Approved Expenses -->
        <div class="flex flex-col border border-[#E4E4E7] border-l-4 border-l-[#16A34A] bg-[#FAFAFA] rounded">
          <div class="flex justify-between items-center py-1.5 px-2 border-b border-[#BBF7D0] rounded-t bg-[#F0FDF4]">
            <p class="text-sm font-bold text-[#15803D]">
              Approved Expenses <span class="font-normal text-zinc-500">(B)</span>
            </p>
            <p class="text-sm font-bold text-[#15803D]">{{ formatToIndianCurrency(approvedExpenses) }}</p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="props.stats.approved_expenses.within_grant == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[14px] h-[14px] bg-[#16A34A] rounded-full"></div>
              <p class="sm-medium">Within Grant Amount</p>
            </div>
            <p class="sm-medium">
              {{
                props.stats.approved_expenses.within_grant == 0
                  ? '-'
                  : formatToIndianCurrency(+props.stats.approved_expenses.within_grant)
              }}
            </p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="props.stats.approved_expenses.exceeding_grant == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[14px] h-[14px] bg-[#B91C1C] rounded-full"></div>
              <p class="sm-medium">Exceeding Grant Amount</p>
            </div>
            <p class="sm-medium text-[#B91C1C]">
              {{
                props.stats.approved_expenses.exceeding_grant == 0
                  ? '-'
                  : formatToIndianCurrency(+props.stats.approved_expenses.exceeding_grant)
              }}
            </p>
          </div>
        </div>

        <!-- Tax Invoice section (enterprise only) -->
        <div
          v-if="enterpriseVersion"
          class="flex flex-col border border-[#E4E4E7] border-l-4 border-l-[#60A5FA] bg-[#FAFAFA] rounded"
        >
          <div
            class="flex justify-between items-start gap-2 py-1.5 px-2 border-b border-[#BFDBFE] rounded-t bg-[#EFF6FF]"
          >
            <p class="text-sm font-bold text-[#3B82F6] leading-snug">
              Approved Expenses
              <span class="font-normal text-zinc-500 text-xs block">Tax Invoice / Proforma</span>
            </p>
            <p class="text-sm font-bold text-[#3B82F6] shrink-0 whitespace-nowrap">
              {{
                formatToIndianCurrency(
                  props.stats.approved_expenses.total_tax_approved_amount +
                    props.stats.unapproved_expenses.total_tax_unapproved_amount
                )
              }}
            </p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="props.stats.approved_expenses.total_tax_approved_amount == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[12px] h-[12px] rounded-full border-2 border-[#60A5FA] bg-[#EFF6FF]"></div>
              <p class="sm-medium">Tax Invoice</p>
            </div>
            <p class="sm-medium text-[#3B82F6]">
              {{
                props.stats.approved_expenses.total_tax_approved_amount == 0
                  ? '-'
                  : formatToIndianCurrency(+props.stats.approved_expenses.total_tax_approved_amount)
              }}
            </p>
          </div>
          <div v-if="enterpriseVersion" class="flex justify-between items-center pl-3 p-1">
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[12px] h-[12px] rounded-full border-2 border-[#60A5FA] bg-[#EFF6FF]"></div>
              <p class="sm-medium">Proforma</p>
            </div>
            <p class="sm-medium text-[#3B82F6]">
              {{
                props.stats.unapproved_expenses.total_tax_unapproved_amount == 0
                  ? '-'
                  : formatToIndianCurrency(+props.stats.unapproved_expenses.total_tax_unapproved_amount)
              }}
            </p>
          </div>
        </div>

        <!-- (C) Expenses On Approval — Pending -->
        <div class="flex flex-col border border-[#E4E4E7] border-l-4 border-l-[#EA580C] bg-[#FAFAFA] rounded">
          <div class="flex justify-between items-center py-1.5 px-2 border-b border-[#FED7AA] rounded-t bg-[#FFF7ED]">
            <p class="text-sm font-bold text-[#C2410C]">
              Expenses On Approval <span class="font-normal text-zinc-500">(C)</span>
            </p>
            <p class="text-sm font-bold text-[#C2410C]">{{ formatToIndianCurrency(pendingExpenses) }}</p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.pending_expenses?.pending_within_grant ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden">
                <div class="approval-dot-within-grant"></div>
              </div>
              <p class="sm-medium">Within Grant Amount</p>
            </div>
            <p class="sm-medium">
              {{
                (props.stats.pending_expenses?.pending_within_grant ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.pending_expenses?.pending_within_grant ?? 0))
              }}
            </p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.pending_expenses?.pending_exceeding_grant ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden border-black">
                <div class="approval-dot-exceeding-grant"></div>
              </div>
              <p class="sm-medium">Exceeding Grant Amount</p>
            </div>
            <p class="sm-medium text-[#B91C1C]">
              {{
                (props.stats.pending_expenses?.pending_exceeding_grant ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.pending_expenses?.pending_exceeding_grant ?? 0))
              }}
            </p>
          </div>
        </div>

        <!-- (D) Expense Draft -->
        <div class="flex flex-col border border-[#E4E4E7] border-l-4 border-l-[#D97706] bg-[#FAFAFA] rounded">
          <div class="flex justify-between items-center py-1.5 px-2 border-b border-[#FDE68A] rounded-t bg-[#FFFBEB]">
            <p class="text-sm font-bold text-[#92400E]">
              Expense Draft <span class="font-normal text-zinc-500">(D)</span>
            </p>
            <p class="text-sm font-bold text-[#92400E]">{{ formatToIndianCurrency(draftExpenses) }}</p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.draft_expenses?.draft_within_grant ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden">
                <div class="draft-dot-within-grant"></div>
              </div>
              <p class="sm-medium">Within Grant Amount</p>
            </div>
            <p class="sm-medium">
              {{
                (props.stats.draft_expenses?.draft_within_grant ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.draft_expenses?.draft_within_grant ?? 0))
              }}
            </p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.draft_expenses?.draft_exceeding_grant ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <div class="flex self-start">
                <TopToRightAngleIcon />
              </div>
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden">
                <div class="draft-dot-exceeding-grant"></div>
              </div>
              <p class="sm-medium">Exceeding Grant Amount</p>
            </div>
            <p class="sm-medium text-[#C2410C]">
              {{
                (props.stats.draft_expenses?.draft_exceeding_grant ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.draft_expenses?.draft_exceeding_grant ?? 0))
              }}
            </p>
          </div>
        </div>

        <!-- Current Balance (A - B) -->
        <div
          class="flex justify-between items-center px-2 py-1.5 rounded border"
          :class="currentBalance < 0 ? 'bg-[#FEF2F2] border-[#FECACA]' : 'bg-[#F0FDF4] border-[#BBF7D0]'"
        >
          <p class="text-sm font-bold">Current Balance <span class="font-normal text-zinc-500">(A - B)</span></p>
          <p class="text-sm font-bold" :class="currentBalance < 0 ? 'text-[#B91C1C]' : 'text-[#15803D]'">
            <span v-if="currentBalance < 0">(-) </span>
            {{ formatToIndianCurrency(Math.abs(currentBalance)) }}
          </p>
        </div>

        <!-- Potential Balance (A - B - C - D) -->
        <div
          class="flex justify-between items-start px-2 py-1.5 rounded border"
          :class="potentialBalance < 0 ? 'bg-[#FEF2F2] border-[#FECACA]' : 'bg-[#F0FDF4] border-[#BBF7D0]'"
        >
          <div class="flex gap-2">
            <div class="w-[14px] h-[14px] bg-[#D4D4D8] rounded-full mt-[3px] shrink-0"></div>
            <div class="flex flex-col">
              <p class="text-sm font-bold">
                Potential Balance <span class="font-normal text-zinc-500">(A - B - C - D)</span>
              </p>
              <p class="text-xs font-normal text-zinc-500">Balance if all expenses are approved</p>
            </div>
          </div>
          <p class="text-sm font-bold" :class="potentialBalance < 0 ? 'text-[#B91C1C]' : 'text-[#15803D]'">
            <span v-if="potentialBalance < 0">(-) </span>
            {{ formatToIndianCurrency(Math.abs(potentialBalance)) }}
          </p>
        </div>
      </div>
    </template>

    <!-- Trigger cell content -->
    <div class="w-full h-full flex flex-col gap-1 items-center text-xs rounded px-1.5 py-1">
      <div class="flex justify-between w-full">
        <div
          class="text-[#166534] font-medium bg-[#F0FDF4] rounded px-1 py-0.5 flex gap-1"
          :class="percentageUtilized > 100 ? 'border border-[#B91C1C]' : ''"
        >
          <p v-if="!isZeroGrant">
            <span class="font-bold">{{ percentageUtilized }}%</span> Utilised
          </p>
          <p v-else>
            <span class="font-bold">-</span>
          </p>
          <ExclamationCircleIcon v-if="percentageUtilized > 100" class="w-4 h-4 text-[#B91C1C]" />
        </div>
        <span class="bg-[#FAFAFA] text-[#18181B] px-1 py-0.5 rounded"
          ><span class="font-bold"
            ><span v-if="currentBalance < 0">(-) </span>{{ formatToIndianCurrency(Math.abs(currentBalance)) }}</span
          >
          Current Bal.</span
        >
      </div>

      <!-- Progress bar: approved_within | pending_within | draft_within | approved_exceeding | pending_exceeding | draft_exceeding -->
      <div class="w-full h-2.5 rounded-full relative overflow-hidden flex border border-[#E4E4E7] p-0.5 bg-[#F4F4F5]">
        <template v-if="isZeroGrant">
          <div
            :class="hasExceedingExpenses ? 'approval-bar-exceeding-grant' : 'approved-bar-exceeding-grant'"
            style="width: 100%; left: 0"
          ></div>
        </template>
        <template v-else>
          <!-- Approved within -->
          <div class="approved-bar-within-grant" :style="{ width: scaledApprovedWithinGrants + '%', left: '0' }"></div>
          <!-- Pending within -->
          <div
            class="approval-bar-within-grant"
            :style="{
              width: scaledPendingWithinGrants + '%',
              left: scaledApprovedWithinGrants + '%'
            }"
          ></div>
          <!-- Draft within -->
          <div
            class="draft-bar-within-grant"
            :style="{
              width: scaledDraftWithinGrants + '%',
              left: scaledApprovedWithinGrants + scaledPendingWithinGrants + '%'
            }"
          ></div>
          <!-- Approved exceeding -->
          <div
            class="approved-bar-exceeding-grant"
            :style="{
              width: scaledApprovedExceedingGrants + '%',
              left: scaledApprovedWithinGrants + scaledPendingWithinGrants + scaledDraftWithinGrants + '%'
            }"
          ></div>
          <!-- Pending exceeding -->
          <div
            class="approval-bar-exceeding-grant"
            :style="{
              width: scaledPendingExceedingGrants + '%',
              left:
                scaledApprovedWithinGrants +
                scaledPendingWithinGrants +
                scaledDraftWithinGrants +
                scaledApprovedExceedingGrants +
                '%'
            }"
          ></div>
          <!-- Draft exceeding -->
          <div
            class="draft-bar-exceeding-grant"
            :style="{
              width: scaledDraftExceedingGrants + '%',
              left:
                scaledApprovedWithinGrants +
                scaledPendingWithinGrants +
                scaledDraftWithinGrants +
                scaledApprovedExceedingGrants +
                scaledPendingExceedingGrants +
                '%'
            }"
          ></div>
        </template>
      </div>

      <!-- Bottom badges: inline row, single full-width light background -->
      <div class="flex w-full bg-[#fff9f3] rounded overflow-hidden">
        <!-- On Approval -->
        <div class="flex items-center gap-1 px-1.5 py-0.5 flex-1 min-w-0">
          <p class="text-[#9A3412] text-xs font-semibold shrink-0">On Approval</p>
          <div class="flex items-center gap-0.5">
            <span class="text-[#9A3412] text-xs font-bold whitespace-nowrap">{{
              formatToIndianCurrencyBadge(pendingExpenses)
            }}</span>
            <ExclamationCircleIcon v-if="pendingExceedingExists" class="w-3.5 h-3.5 text-[#B91C1C] shrink-0" />
          </div>
        </div>
        <!-- Divider + Draft (always shown) -->
        <div class="w-px bg-[#9A3412] shrink-0 my-1"></div>
        <div class="flex-1 flex items-center gap-1 px-1.5 py-0.5 min-w-0">
          <p class="text-[#9a3412d5] text-xs font-semibold shrink-0">Draft</p>
          <div class="flex items-center gap-0.5">
            <span class="text-[#9A3412] text-xs font-bold whitespace-nowrap">{{
              formatToIndianCurrencyBadge(draftExpenses)
            }}</span>
            <ExclamationCircleIcon v-if="draftExceedingExists" class="w-3.5 h-3.5 text-[#9A3412] shrink-0" />
          </div>
        </div>
      </div>
    </div>
  </a-popover>
</template>

<style>
  .ant-popover .ant-popover-inner {
    padding: 0 !important;
  }
</style>

<style scoped>
  .approved-bar-within-grant {
    height: 100%;
    background-color: #16a34a;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approval-bar-within-grant {
    height: 100%;
    background: repeating-linear-gradient(125deg, #15803d, #15803d 4px, transparent 4px, transparent 8px), #16a34a;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .draft-bar-within-grant {
    height: 100%;
    background: repeating-linear-gradient(45deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 6px),
      repeating-linear-gradient(135deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 6px), #16a34a;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approved-bar-exceeding-grant {
    height: 100%;
    background-color: #b91c1c;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approval-bar-exceeding-grant {
    height: 100%;
    background: repeating-linear-gradient(125deg, #991b1b, #991b1b 4px, transparent 4px, transparent 8px), #b91c1c;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .draft-bar-exceeding-grant {
    height: 100%;
    background: repeating-linear-gradient(45deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 6px),
      repeating-linear-gradient(135deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 6px), #b91c1c;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approval-dot-within-grant {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(125deg, #15803d, #15803d 3px, transparent 3px, transparent 6px), #16a34a;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  .approval-dot-exceeding-grant {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(125deg, #991b1b, #991b1b 3px, transparent 3px, transparent 6px), #b91c1c;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  .draft-dot-within-grant {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(45deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 5px),
      repeating-linear-gradient(135deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 5px), #16a34a;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  .draft-dot-exceeding-grant {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(45deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 5px),
      repeating-linear-gradient(135deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 5px), #b91c1c;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  @keyframes fillWidth {
    from {
      transform: scaleX(0);
      transform-origin: left;
    }

    to {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
</style>
